import { isSupabaseConfigured, missingSupabaseConfig } from "./config";
import { supabase } from "./client";
import { withRequestDeadline } from "./requestDeadline";
import { invalidateIngredientImage } from "./ingredientImages";

const INGREDIENT_FIELDS = "id,company_id,name,price,type,selectable,created_at,starter_template_key";

const DB_TYPE_BY_UI = {
  obligatorio: "required",
  opcional: "optional",
};

const UI_TYPE_BY_DB = {
  required: "obligatorio",
  optional: "opcional",
};

const ensureSupabaseReady = () => {
  if (!isSupabaseConfigured || !supabase) {
    throw new Error(
      `Faltan variables de entorno para Supabase: ${missingSupabaseConfig.join(", ")}`
    );
  }
};

const ensureCompanyId = (companyId) => {
  if (!companyId) {
    throw new Error("No encontramos una empresa activa para trabajar con ingredientes.");
  }
};

const normalizeError = (error, fallbackMessage) => {
  if (!error) {
    return new Error(fallbackMessage);
  }

  if (error.code === "42501") {
    return new Error(
      "Supabase rechazo el acceso. Revisa el login o las politicas RLS del proyecto."
    );
  }

  return new Error(error.message || fallbackMessage);
};

const mapRowToIngrediente = (row, imageScope = "") => ({
  id: row.id,
  nombre: row.name || "",
  imagen: row.image_url || "",
  precio: String(row.price ?? 0),
  tipo: UI_TYPE_BY_DB[row.type] || "opcional",
  seleccionable: row.selectable !== false,
  createdAt: row.created_at || null,
  companyId: row.company_id || null,
  starterTemplateKey: row.starter_template_key || null,
  ...(!Object.prototype.hasOwnProperty.call(row, "image_url") ? {
    imageDeferred: true,
    imageCompanyId: row.company_id,
    imageScope,
    imageVersion: row.created_at || "",
  } : {}),
});

const mapIngredienteToRow = (companyId, ingrediente) => ({
  company_id: companyId,
  name: ingrediente.nombre.trim(),
  ...(Object.prototype.hasOwnProperty.call(ingrediente, "imagen")
    ? { image_url: ingrediente.imagen || "" } : {}),
  price: Number(ingrediente.precio || 0),
  type: DB_TYPE_BY_UI[ingrediente.tipo] || "optional",
  selectable: Boolean(ingrediente.seleccionable),
  active: true,
});

export const listIngredientes = async (companyId, { includeImages = true, imageScope = "" } = {}) => {
  ensureSupabaseReady();
  ensureCompanyId(companyId);

  const { data, error } = await withRequestDeadline(async (signal) => {
    const rows = [];
    for (let offset = 0; ; offset += 500) {
      let query = supabase.from("ingredients")
        .select(includeImages ? "*" : INGREDIENT_FIELDS)
        .eq("company_id", companyId)
        .order("created_at", { ascending: false });
      if (!includeImages) query = query.order("id").range(offset, offset + 499);
      const result = await query.abortSignal(signal);
      if (result.error) return result;
      rows.push(...(result.data || []));
      if (includeImages || (result.data || []).length < 500) return { data: rows };
      if (signal.aborted) throw new Error("La carga de ingredientes fue cancelada.");
    }
  }, {
      timeoutMs: 15000,
      message: "La carga de ingredientes tardo demasiado. Puedes volver a intentar sin perder tus cambios.",
    });

  if (error) {
    throw normalizeError(error, "No se pudieron cargar los ingredientes desde Supabase.");
  }

  return (data || []).map((row) => mapRowToIngrediente(row, imageScope));
};

export const createIngrediente = async (companyId, ingrediente) => {
  ensureSupabaseReady();
  ensureCompanyId(companyId);

  const { data, error } = await supabase
    .from("ingredients")
    .insert(mapIngredienteToRow(companyId, ingrediente))
    .select(INGREDIENT_FIELDS)
    .single();

  if (error) {
    throw normalizeError(error, "No se pudo guardar el ingrediente en Supabase.");
  }

  return { ...mapRowToIngrediente(data), imagen: ingrediente.imagen || "" };
};

export const updateIngrediente = async (companyId, id, ingrediente) => {
  ensureSupabaseReady();
  ensureCompanyId(companyId);

  const { data, error } = await supabase
    .from("ingredients")
    .update(mapIngredienteToRow(companyId, ingrediente))
    .eq("id", id)
    .eq("company_id", companyId)
    .select(INGREDIENT_FIELDS)
    .single();

  if (error) {
    throw normalizeError(error, "No se pudo actualizar el ingrediente en Supabase.");
  }

  if (Object.prototype.hasOwnProperty.call(ingrediente, "imagen")) {
    invalidateIngredientImage(companyId, id);
  }
  return { ...mapRowToIngrediente(data), imagen: ingrediente.imagen || "" };
};

export const deleteIngrediente = async (companyId, id) => {
  ensureSupabaseReady();
  ensureCompanyId(companyId);

  const { error } = await supabase
    .from("ingredients")
    .delete()
    .eq("id", id)
    .eq("company_id", companyId);

  if (error) {
    throw normalizeError(error, "No se pudo eliminar el ingrediente en Supabase.");
  }

  invalidateIngredientImage(companyId, id);
  return id;
};
